import {Controller} from "@hotwired/stimulus"

// Connects to data-controller="voucher-resource"
export default class extends Controller {
  static values = {view: String}
  static targets = ["projectIdSelectInput", "entitlementSelectInput"]

  initialize() {
    this.prepareStorage()
  }

  connect() {
    const entitlementSelect = this.entitlementSelect
    if (!entitlementSelect) return

    if (Object.keys(this.entitlements).length === 0) {
      this.extractEntitlementsFromOptions()
    }

    this.updateOptionsForProject(this.projectIdFromSelect)
  }

  disconnect() {
    this.prepareStorage()
  }

  // Stimulus targets may not always be present (different Avo views), so provide fallbacks.
  get projectSelect() {
    if (this.hasProjectIdSelectInputTarget) return this.projectIdSelectInputTarget
    const form = this.element.querySelector("form")
    return (form || this.element).querySelector('select[name$="[voucher_project_id]"]')
  }

  get entitlementSelect() {
    if (this.hasEntitlementSelectInputTarget) return this.entitlementSelectInputTarget
    const form = this.element.querySelector("form")
    return (form || this.element).querySelector('select[name$="[voucher_entitlement]"]')
  }

  get projectIdFromSelect() {
    const select = this.projectSelect
    return select ? select.value : null
  }

  onProjectChange(event) {
    const select = event?.target || this.projectSelect
    const projectId = select ? select.value : null
    this.updateOptionsForProject(projectId)
  }

  extractEntitlementsFromOptions() {
    const entitlementSelect = this.entitlementSelect
    if (!entitlementSelect) return

    this.prepareStorage()

    for (const option of entitlementSelect.options) {
      if (!option.value) {
        if (!this.blankEntitlementOption) {
          this.blankEntitlementOption = {
            value: option.value,
            text: option.text
          }
        }
        continue
      }

      const entitlement = this.extractEntitlementFromOption(option)
      if (!entitlement) continue

      this.storeEntitlement(entitlement.projectId, entitlement)
      this.storeEntitlement(entitlement.normalizedProjectId, entitlement)
      this.storeEntitlement(`proj${entitlement.normalizedProjectId}`, entitlement)
      this.storeEntitlement(`proj_${entitlement.normalizedProjectId}`, entitlement)
    }

    this.entitlements.__all__ = [...this.allEntitlements]
    this.removeEntitlementSelectOptions()
  }

  storeEntitlement(key, entitlement) {
    if (!key) return

    if (!this.entitlements[key]) {
      this.entitlements[key] = []
    }

    if (!this.entitlements[key].some(item => item.value === entitlement.value)) {
      this.entitlements[key].push(entitlement)
    }

    if (!this._seenEntitlementValues.has(entitlement.value)) {
      this.allEntitlements.push(entitlement)
      this._seenEntitlementValues.add(entitlement.value)
    }
  }

  updateOptionsForProject(projectId) {
    const entitlementSelect = this.entitlementSelect
    if (!entitlementSelect) return

    const entitlements = this.lookupEntitlements(projectId)

    this.removeEntitlementSelectOptions()

    if (this.blankEntitlementOption) {
      const shouldSelectBlank = !projectId
      const blankOption = this.createOption(
        this.blankEntitlementOption.value,
        this.blankEntitlementOption.text,
        shouldSelectBlank
      )
      entitlementSelect.appendChild(blankOption)
    }

    for (const entitlement of entitlements) {
      const option = this.createOption(
        entitlement.value,
        entitlement.text,
        entitlement.selected
      )
      entitlementSelect.appendChild(option)
    }

    const firstOption = entitlementSelect.options[0]
    if (firstOption && !Array.from(entitlementSelect.options).some(option => option.selected)) {
      firstOption.selected = true
    }
  }

  removeEntitlementSelectOptions() {
    const entitlementSelect = this.entitlementSelect
    if (entitlementSelect) entitlementSelect.innerHTML = ""
  }

  extractEntitlementFromOption(option) {
    const match = option.text.match(/\[(.*?)\]$/)
    if (!match) return null

    const projectId = match[1]
    return {
      value: option.value,
      text: option.text.replace(/\s*\[.*?\]$/, "").trim(),
      projectId,
      normalizedProjectId: this.normalizeProjectId(projectId),
      selected: option.selected
    }
  }

  lookupEntitlements(projectId) {
    if (!projectId) return this.entitlements.__all__ || []

    const normalized = this.normalizeProjectId(projectId)
    const keys = [projectId, normalized, `proj${normalized}`, `proj_${normalized}`]

    for (const key of keys) {
      if (key && this.entitlements[key]) {
        return this.entitlements[key]
      }
    }

    return this.entitlements.__all__ || []
  }

  normalizeProjectId(projectId) {
    if (!projectId) return projectId
    return projectId.replace(/^proj[_-]?/i, "")
  }

  createOption(value, text, selected = false) {
    const option = document.createElement("option")
    option.value = value
    option.text = text

    if (selected || text.startsWith("All PRO features")) {
      option.selected = true
    }

    return option
  }

  prepareStorage() {
    this.entitlements = {}
    this.allEntitlements = []
    this.blankEntitlementOption = null
    this._seenEntitlementValues = new Set()
  }
}
